import { useState, useCallback } from 'react'
import { Sync, GetApp } from '@mui/icons-material'
import { Drawer as MuiDrawer, List, IconButton, Tooltip } from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import { isEmpty } from 'lodash'
import { FeatureDict, selectFeatureIncludes } from '@authed/selectors'
import { DatasetStatus } from '@components'
import { ListLoader } from '@components/skeletonLoader/listLoader'
import { useAssignmentsExecution } from '@datasets/dataset-details/datasetHistoryV2/hooks/useAssignmentsExecution'
import { useTargets } from '@datasets/dataset-details/datasetHistoryV2/hooks/useTargets'
import {
  DatasetSubmissionOutboundIntegration,
  ITargetRow,
  SubmissionWithModDatasetPosts,
} from '@datasets/dataset-details/datasetHistoryV2/types'
import {
  FILE_BASE_INTEGRATIONS,
  outboundIntegrationHasHistory,
} from '@datasets/dataset-details/datasetHistoryV2/utils'
import { showSnackbar } from '@global/snackbar/snackbar.actions'
import { useAppSelector, useAppDispatch } from '@redux/hooks'
import { api } from 'api'
import { Actions } from './components/actions'
import { Header } from './components/header'
import { IntegrationAssignmentItem } from './components/integrationAssignmentItem'
import { IntegrationStatus } from './components/integrationItem'
import { Targets } from './components/targets'
import * as css from './styled'
import { t } from '@lingui/core/macro'

interface DrawerProps {
  open: boolean
  onClose: () => void
  selectedOutbound: DatasetSubmissionOutboundIntegration | null
  selectedTargetRow: ITargetRow | null
  syncButtonIsDisabled: boolean
  datasetSubmissionId: number
  handleSyncOutbound: (outbound: DatasetSubmissionOutboundIntegration) => void
  getSelectedSubmissionData: () => SubmissionWithModDatasetPosts | undefined
}

async function downloadIntegration(execution) {
  const { data }: { data: string } = await api.post(
    '/api/v1/integration-assignments/execution/file/',
    {
      file: execution.id,
    },
  )
  window.location.href = data
}

export const Drawer = ({
  open,
  selectedOutbound,
  selectedTargetRow,
  onClose,
  syncButtonIsDisabled,
  handleSyncOutbound,
  datasetSubmissionId,
  getSelectedSubmissionData,
}: DrawerProps) => {
  const reduxDispatch = useAppDispatch()
  const [API_DEBUG_ENABLED = false, RERUN_INTEGRATIONS_ENABLED = false] = useAppSelector(
    selectFeatureIncludes([
      FeatureDict.DATASET_DETAILS_API_DEBUG,
      FeatureDict.RERUN_INTEGRATIONS_ENABLED,
    ]),
  )
  const [openedIntegrations, setOpenedIntegrations] = useState<Record<number, boolean>>({})
  const [openedExecutions, setOpenedExecutions] = useState<Record<number, boolean>>({})
  const [openedTargets, setOpenedTargets] = useState({})
  const [sendingRerunOfIntegration, setSendingRerunOfIntegration] = useState<
    Record<number, boolean>
  >({})

  const queryClient = useQueryClient()
  const slug = selectedOutbound?.slug || selectedTargetRow?.slug
  const hasOrchestrationHistory = outboundIntegrationHasHistory(selectedOutbound)
  const isFileBaseSupported =
    FILE_BASE_INTEGRATIONS.includes(slug || '') && !hasOrchestrationHistory
  const isIntegrationAssignmentSupported =
    !FILE_BASE_INTEGRATIONS.includes(selectedOutbound?.slug || '') || hasOrchestrationHistory

  const selectedAssignments = selectedOutbound?.integrationAssignments

  const { assignmentsExecution, getOutboundStatus, isLoadingAssignmentExecution } =
    useAssignmentsExecution(datasetSubmissionId)

  const { getTargetStatus } = useTargets()
  const { success: successfulTargetPosts, total: totalTargetPosts } = getTargetStatus(
    selectedTargetRow?.datasetPosts,
  )

  const { success: successfulIntegrationAssignments, total: totalIntegrationAssignments } =
    getOutboundStatus(selectedOutbound?.integrationAssignments)

  const handleToggleIntegrationLogs = useCallback((id: number) => {
    setOpenedIntegrations(prev => ({ ...prev, [id]: !prev[id] }))
  }, [])

  const handleToggleAssignmentExecs = useCallback((id: number) => {
    setOpenedExecutions(prev => ({ ...prev, [id]: !prev[id] }))
  }, [])

  /**
   * Allow the re-running of an integration.
   * - Invalidate the integration assignmenet execution query to refetch the data.
   * - Show user-friendly message if the re-run fails (500 server error).
   * @param execution
   * @param submission
   */
  async function rerunIntegration(execution, submission) {
    const { id: execId } = execution
    const { id: subId } = submission

    setSendingRerunOfIntegration(prev => ({ ...prev, [execId]: true }))

    try {
      await api.post(`/api/v1/account/dataset/submission/triggerOutputConfigurations`, {
        submissionIds: [subId],
        integrationExecutionIds: [execId],
      })

      // Invalidate the query to refetch the data.
      void queryClient.invalidateQueries({
        queryKey: ['integration-assignmnents-execution', datasetSubmissionId],
      })
      setSendingRerunOfIntegration(prev => ({ ...prev, [execId]: false }))
    } catch (error: any) {
      setSendingRerunOfIntegration(prev => ({ ...prev, [execId]: false }))

      const status = error?.status
      const statusText = error?.statusText
      const errorMessage = status === 500 ? `Error: ${status} ${statusText} | ` : ''

      reduxDispatch(
        showSnackbar({
          message: `${errorMessage}There was an issue re-running the integration. Try again or contact your CSM.`,
          type: 'ERROR',
        }),
      )
    }
  }

  const submission = getSelectedSubmissionData()
  const submissionStatusIsComplete = submission?.status === DatasetStatus.COMPLETE
  const isAllSyncsSuccessful = selectedOutbound
    ? successfulIntegrationAssignments === totalIntegrationAssignments && submissionStatusIsComplete
    : successfulTargetPosts === totalTargetPosts && submissionStatusIsComplete
  const enableResyncAll = !isEmpty(selectedOutbound) && isEmpty(selectedTargetRow) // Resync is not supported on Targets.

  const { name } = selectedTargetRow || {}

  // Check if the execution is successful by checking if the output configuration error key exists on the assignment.
  const isExecutionSuccessful = x => !Object.keys(x)?.includes('Output Configuration Error')

  // FIXME: Submissions dataset_posts left over from the targetRows that have slugs, should it show in targets?

  return (
    <MuiDrawer anchor="right" open={Boolean(open)} onClose={onClose}>
      <css.FlexWrapper>
        <Header name={name} slug={slug} />
        <css.DrawerContent>
          <Actions
            enableResyncAll={enableResyncAll}
            isAllSyncsSuccessful={isAllSyncsSuccessful}
            totalSuccessful={
              selectedOutbound ? successfulIntegrationAssignments : successfulTargetPosts
            }
            totalAssignments={selectedOutbound ? totalIntegrationAssignments : totalTargetPosts}
            resyncButtonProps={{
              onClick: () => {
                if (selectedOutbound) {
                  handleSyncOutbound(selectedOutbound)
                }
              },
              disabled: (!selectedTargetRow && !selectedOutbound) || syncButtonIsDisabled,
            }}
          />
          {submission?.id ? (
            <Targets
              submissionId={submission?.id}
              datasetPosts={selectedTargetRow?.datasetPosts || []}
              open={openedTargets}
              onClick={setOpenedTargets}
              assignmentsExecution={assignmentsExecution || {}}
              integrationName={name}
            />
          ) : null}
          {isIntegrationAssignmentSupported ? (
            <List sx={{ marginTop: '10px' }}>
              {selectedAssignments?.map(integrationAssignment => (
                <IntegrationAssignmentItem
                  integrationAssignment={integrationAssignment}
                  key={integrationAssignment.id}
                  open={Boolean(openedIntegrations[integrationAssignment.id])}
                  onClick={() => {
                    handleToggleIntegrationLogs(integrationAssignment.id)
                  }}
                />
              ))}
            </List>
          ) : null}
          {isLoadingAssignmentExecution && isFileBaseSupported ? <ListLoader /> : null}
          {!isLoadingAssignmentExecution && isFileBaseSupported ? (
            <IntegrationStatus
              assignments={selectedAssignments || []}
              executions={assignmentsExecution || {}}
            >
              {x => (
                <IntegrationStatus.Item
                  execution={x}
                  isOpen={Boolean(openedExecutions[x.id])}
                  onClick={() => {
                    handleToggleAssignmentExecs(x.id)
                  }}
                >
                  {RERUN_INTEGRATIONS_ENABLED ? (
                    <IconButton
                      size="small"
                      color="info"
                      aria-label="rerun integration"
                      onClick={e => {
                        e.stopPropagation()
                        void rerunIntegration(x, submission)
                      }}
                      disabled={sendingRerunOfIntegration[x.id]}
                    >
                      <Tooltip title={t`Resync integration`}>
                        <Sync css={sendingRerunOfIntegration[x.id] && css.rotateIconStyle} />
                      </Tooltip>
                    </IconButton>
                  ) : null}
                  {API_DEBUG_ENABLED && isExecutionSuccessful(x) ? (
                    <IconButton
                      size="small"
                      color="info"
                      aria-label="download"
                      onClick={e => {
                        e.stopPropagation()
                        void downloadIntegration(x)
                      }}
                    >
                      <Tooltip title={t`Download file`}>
                        <GetApp />
                      </Tooltip>
                    </IconButton>
                  ) : null}
                </IntegrationStatus.Item>
              )}
            </IntegrationStatus>
          ) : null}
        </css.DrawerContent>
      </css.FlexWrapper>
    </MuiDrawer>
  )
}
